import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useSafeUser from '@/hooks/useSafeUser';
import { useUser } from '@/contexts/UserContext/UserContext';

interface AuthenticatedPageOptions {
  allowIncompleteProfile?: boolean;
  redirectPath?: string;
}

export default function useAuthenticatedPage(options?: AuthenticatedPageOptions) {
  const { isAuthenticated: isAuth0Authenticated, isLoading: isAuth0Loading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSafeUser() as any;
  const { isLoading: isUserLoading, error } = useUser();
  const navigationAttemptCount = useRef(0); // Track navigation attempts to prevent loops

  const { allowIncompleteProfile = false, redirectPath = '/complete-profile' } = options || {};

  useEffect(() => {
    // Prevent infinite navigation loops by limiting attempts
    if (navigationAttemptCount.current > 5) {

      return;
    }

    // Wait for authentication state to be determined and user data to load
    if (isAuth0Loading || isUserLoading) {
      return;
    }

    // Handle backend error
    if (error) {
      navigate('/backend-down');
      return;
    }

    // If not authenticated, redirect to login
    if (!isAuth0Authenticated) {
      navigate('/');
      return;
    }

    // If authenticated but user data is still null, wait for it to load
    if (isAuth0Authenticated && user === null) {
      return;
    }

    // If authenticated and user data is available, make routing decision once
    if (isAuth0Authenticated && user) {
      const hasCompletedProfileSetup = localStorage.getItem('hasCompletedProfileSetup');

      // If setup is already marked as complete, go to links (unless we're in setup flow)
      if (hasCompletedProfileSetup === 'true') {
        // But don't redirect if we're already in the setup flow
        const setupPaths = ['/profile-title', '/complete-profile', '/choose-template', '/add-links', '/preview-deploy'];
        if (setupPaths.includes(location.pathname)) {
          return;
        }

        if (location.pathname !== '/links') {
          navigationAttemptCount.current += 1;
          navigate('/links', { replace: true });
        }
        return;
      }
      const isProfileComplete = user.title && user.profileComplete;
      const hasTemplate = user.template !== null && user.template !== undefined;

      // If user has completed all steps, redirect to links page and set the flag
      if (isProfileComplete && hasTemplate) {
        // Set the flag that the user has completed the full setup
        localStorage.setItem('hasCompletedProfileSetup', 'true');
        if (location.pathname !== '/links') {
          navigationAttemptCount.current += 1;
          navigate('/links', { replace: true });
        }
        return;
      }

      // Determine the correct destination based on user state
      let targetPath = '/links'; // Default destination

      if (!user.title) {
        targetPath = '/profile-title';
      } else if (user.profileComplete === false) {
        targetPath = '/complete-profile';
      } else if (user.template === null || user.template === undefined) {
        // User has title and completed profile but no template selected
        targetPath = '/choose-template';
      } else {
        // User has completed all steps including template selection
        // Set the flag that the user has completed the full setup
        localStorage.setItem('hasCompletedProfileSetup', 'true');
        targetPath = '/links';
      }

      // Only navigate if we're not already at the target path
      // and we're not in the setup flow (let page components handle that)
      const setupPaths = ['/profile-title', '/complete-profile', '/choose-template', '/add-links', '/preview-deploy'];
      if (!setupPaths.includes(location.pathname) && location.pathname !== targetPath) {
        navigationAttemptCount.current += 1;
        navigate(targetPath, { replace: true });
      }
    }
  }, [
    isAuth0Authenticated,
    isAuth0Loading,
    user,
    isUserLoading,
    navigate,
    location.pathname,
    allowIncompleteProfile,
    redirectPath,
    error,
  ]);
}